import { useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import type { Moment } from 'moment';
import clsx from 'clsx';
import { localize } from '@oneflowab/pomes';

import { errorReporter } from '@flow/logging';
import {
  getMoment,
  formatDateString,
  hasDatePassed,
  getTranslationForDateFormat,
} from '@flow/libs/date';
import type { DateFormat } from 'types/date';

import { isSignLaterEnabled } from '@flow/libs/agreement';
import useAgreement from '@app/hooks/use-agreement';
import agreements from '@app/reducers/entities/agreements';

import { T } from '@app/components/translation';
import { SingleDatePickerComponent } from '@app/components/single-date-picker';

import style from './published-document-expiry-date-picker.module.scss';

type Props = {
  agreementId: string;
  status: string;
  expireDate: string | null;
  dateFormat: DateFormat;
  readOnly: boolean;
  failureToast: ReactNode;
  setFailureReason: (reason: string) => void;
  message: (params: { id: DateFormat; comment: string }) => string;
};

const isOutsideRange = (day: Moment) => day.isBefore(moment().startOf('day'));

const getDocumentPeriodMessage = (agreement: Oneflow.Agreement, expireDate: string | null) => {
  if (!expireDate) {
    return (
      <T
        _str="The document can be signed at any time"
        _comment="Text explains that the signing period doesn't expire"
      />
    );
  }

  if (isSignLaterEnabled(agreement)) {
    return (
      <T
        _str="The document will be expired "
        _comment="Shows at what date the document will be expired"
      />
    );
  }

  return (
    <T
      _str="The document can be signed until"
      _comment="Shows the number of days signing is possible"
    />
  );
};

const PublishedDocumentExpiryDatePickerComponent = ({
  agreementId,
  status,
  expireDate,
  dateFormat,
  readOnly,
  failureToast,
  setFailureReason,
  message,
}: Props) => {
  const dispatch = useDispatch();
  const overdue = status === 'overdue';
  const hasExpireDatePassed = overdue || hasDatePassed(expireDate);

  const [expireDateValue, setExpireDateValue] = useState(() =>
    expireDate === null ? null : getMoment(expireDate).unix(),
  );

  const signingPeriodHeader = (
    <h3
      className={style.SigningPeriodHeader}
      id="signing-period"
    >
      <T
        _str="Signing period"
        _comment="header text for signing period info"
      />
    </h3>
  );

  const updateExpireDate = useCallback(
    (value: number | null) => {
      let date = null;

      if (value !== null) {
        date = moment.unix(value).format('YYYY-MM-DD');
      }

      dispatch(
        agreements.updateExpiryDate({
          id: Number(agreementId),
          data: {
            expireDate: date,
          },
          pipe: {
            onFailure: (reason) => {
              // TO-DO: Toast needed to be replaced here OF-13200
              setFailureReason(reason);
              // Hide it after 10 seconds
              setTimeout(() => {
                setFailureReason('');
              }, 10000);
              errorReporter.error(reason, {
                errorContext: 'Attempt to update signing period as fixed date in contract view',
              });
            },
          },
        }),
      );
    },
    [
      agreementId,
      dispatch,
      setFailureReason,
    ],
  );

  const agreement = useAgreement(Number(agreementId));

  const dateElement = () => {
    if (!readOnly) {
      return (
        <>
          {failureToast}
          <SingleDatePickerComponent
            block={false}
            customClassName={style.ExpiryDatePicker}
            displayFormat={dateFormat}
            input={{
              value: expireDateValue,
              onChange: (value: number | null) => {
                setExpireDateValue(value);
                updateExpireDate(value);
              },
            }}
            isOutsideRange={isOutsideRange}
            label={getDocumentPeriodMessage(agreement, expireDate)}
            placeholder={getTranslationForDateFormat({
              message,
              dateFormat,
            })}
          />
        </>
      );
    }

    if (expireDate) {
      return (
        <span
          className={clsx(style.Bold, {
            [style.DatePassed]: hasExpireDatePassed,
          })}
        >
          {getDocumentPeriodMessage(agreement, expireDate)}
          &nbsp;
          {formatDateString(expireDate, dateFormat)}
        </span>
      );
    }

    return getDocumentPeriodMessage(agreement, expireDate);
  };

  return (
    <div
      className={clsx({
        [style.NoExpiryInfoContainer]: !expireDate,
      })}
      data-testid="read-only-expiry-info"
    >
      {signingPeriodHeader}
      {dateElement()}
    </div>
  );
};

export const PublishedDocumentExpiryDatePicker = localize(
  PublishedDocumentExpiryDatePickerComponent,
);
